import * as yup from 'yup';
export interface ProductCatalogItemsParams {
  alternateItemIdContains?: string;
  offset: number;
  take: number;
  includeInactiveData?: boolean;
  returnInactiveDataOnly?: boolean;
  includeDeletedData?: boolean;
  returnDeletedDataOnly?: boolean;
  productNumberContains?: string;
  productNumbers?: string;
  isCommonStock?: boolean;
  isWebOrderDimPermissionsOverageDefault?: boolean;
  isOverridden?: boolean;
  defaultOverageConditionContains?: string;
  defaultOverageDispositionContains?: string;
  defaultOverageOwnerContains?: string;
  defaultConfigTypeContains?: string;
  deploymentTypeContains?: string;
  // For Url Purposes
}
export type PricingRules = {
  listPrice: number;
  type: string;
  condition: string;
  effectiveDate: string;
  endDate: string;
  discountPercentage: number;
  unitPrice: number;
};
export type PricingRulesImport = {
  productNumber: string;
  listPrice: number;
  type: string;
  condition: string;
  effectiveDate: string;
  endDate: string;
  discountPercentage: number;
  unitPrice: number;
};

export interface ProductCatalogItemPayload {
  category: string;
  defaultConfigType: string;
  defaultOverageOwner?: string;
  deploymentType: string;
  isCommonStock: boolean;
  manufacturer?: string;
  defaultOverageCondition?: string;
  defaultOverageDisposition?: string;
  productName: string;
  productNumber: string;
  isOverridden: boolean;
  isActive: boolean;
  customerXRef?: {
    alternateItemId?: string;
    alternateItemIdLabel?: string;
    customerStandardCost?: number | string;
    modifiedDateTime?: string;
  };
}
export interface AssignRulePayload {
  defaultConfigType?: string;
  defaultOverageOwner?: string;
  deploymentType: string;
  isCommonStock?: boolean;
  defaultOverageCondition?: string;
  defaultOverageDisposition?: string;
  isOverridden?: boolean;
  isActive?: boolean;
  customerXRef?: {
    alternateItemId?: string;
    alternateItemIdLabel?: string;
    customerStandardCost?: number | string;
    modifiedDateTime?: string;
  };
}

export enum ProductCatalogItemLabels {
  category = 'Category',
  defaultConfigType = 'Config Type',
  defaultOverageOwner = 'Default Overage Owner',
  deploymentType = 'Deployment Type',
  isCommonStock = 'Is Common Stock',
  manufacturer = 'Manufacturer',
  defaultOverageCondition = 'Default Overage Condition',
  defaultOverageDisposition = 'Default Overage Disposition',
  productName = 'Product Name',
  productNumber = 'Product Number',
  salesCertReconPrice = 'Certification Sales Price',
  salesNewPrice = 'New Sales Price',
  salesUsedPrice = 'Used Sales Price',
  isWebOrderDimPermissionsOverageDefault = 'Weborder permissions',
  isOverridden = 'Is Overridden'
}

export enum DeploymentTypes {
  AllOrders = 'AllOrders',
  StandardOrder = 'StandardOrder',
  SparingOrder = 'SparingOrder'
}

export const DeploymentTypesLabels = {
  AllOrders: 'All Orders',
  StandardOrder: 'Standard Order',
  SparingOrder: 'Sparing Order'
} as const;

export const productCatalogItemPayloadSchema: yup.SchemaOf<ProductCatalogItemPayload> = yup.object({
  defaultConfigType: yup.string().label(ProductCatalogItemLabels.defaultConfigType).required(),
  defaultOverageOwner: yup.string().label(ProductCatalogItemLabels.defaultOverageOwner).notRequired(),
  deploymentType: yup.string().label(ProductCatalogItemLabels.deploymentType).required(),
  isCommonStock: yup.boolean().label(ProductCatalogItemLabels.isCommonStock).required(),
  defaultOverageCondition: yup.string().label(ProductCatalogItemLabels.defaultOverageCondition).notRequired(),
  defaultOverageDisposition: yup.string().label(ProductCatalogItemLabels.defaultOverageDisposition).notRequired(),
  productNumber: yup.string().label(ProductCatalogItemLabels.productNumber).required(),
  productName: yup.string().label(ProductCatalogItemLabels.productName).required(),
  category: yup.string().label(ProductCatalogItemLabels.category).required(),
  manufacturer: yup.string().label(ProductCatalogItemLabels.manufacturer),
  isWebOrderDimPermissionsOverageDefault: yup.boolean().label(ProductCatalogItemLabels.isWebOrderDimPermissionsOverageDefault),
  isOverridden: yup.boolean().label(ProductCatalogItemLabels.isOverridden).required(),
  isActive: yup.boolean().required(),
  customerXRef: yup.object()
});

export const productCatalogItemImportPayloadSchema: yup.SchemaOf<
  Omit<ProductCatalogItemPayload, 'isActive' | 'isOverridden' | 'productName' | 'manufacturer' | 'category' | 'defaultConfigType'> & { defaultConfigType?: string }
> = yup.object({
  defaultConfigType: yup.string().label(ProductCatalogItemLabels.defaultConfigType).notRequired(),
  defaultOverageOwner: yup.string().label(ProductCatalogItemLabels.defaultOverageOwner).required(),
  deploymentType: yup.string().label(ProductCatalogItemLabels.deploymentType).required(),
  isCommonStock: yup.boolean().label(ProductCatalogItemLabels.isCommonStock).required(),
  defaultOverageCondition: yup.string().label(ProductCatalogItemLabels.defaultOverageCondition).required(),
  defaultOverageDisposition: yup.string().label(ProductCatalogItemLabels.defaultOverageDisposition).required(),
  productNumber: yup.string().label(ProductCatalogItemLabels.productNumber).required(),
  isWebOrderDimPermissionsOverageDefault: yup.boolean().label(ProductCatalogItemLabels.isWebOrderDimPermissionsOverageDefault),
  customerXRef: yup.object()
});
export const productCatalogItemPricingImportPayloadSchema: yup.SchemaOf<PricingRulesImport> = yup.object({
  productNumber: yup.string().label('Product Number').required(),
  type: yup.string().label('Type').required(),
  condition: yup.string().label('Condition').required(),
  effectiveDate: yup.string().label('Effective Date').required(),
  endDate: yup.string().label('End Date').required(),
  discountPercentage: yup.number().label('Discount Percentage').required(),
  unitPrice: yup.number().label('Unit Price').required(),
  listPrice: yup.number().label('List Price').required()
});

export type AssignRuleForm = Omit<ProductCatalogItemPayload, 'productName' | 'productNumber' | 'manufacturer' | 'category'>;

export const assignRuleFormSchema: yup.SchemaOf<AssignRulePayload> = yup.object({
  // The legal Config Type codes come from D365 at runtime (see `useConfigTypes`), so they cannot be
  // listed in a static schema. The restriction is enforced where the list is in hand: the select
  // offers only fetched codes, and the import validates each row against them.
  defaultConfigType: yup.string().label(ProductCatalogItemLabels.defaultConfigType),
  defaultOverageOwner: yup.string().label(ProductCatalogItemLabels.defaultOverageOwner),
  deploymentType: yup.string().label(ProductCatalogItemLabels.deploymentType).required(),
  isCommonStock: yup.boolean().label(ProductCatalogItemLabels.isCommonStock),
  defaultOverageCondition: yup.string().label(ProductCatalogItemLabels.defaultOverageCondition),
  defaultOverageDisposition: yup.string().label(ProductCatalogItemLabels.defaultOverageDisposition),
  isWebOrderDimPermissionsOverageDefault: yup.boolean().label(ProductCatalogItemLabels.isWebOrderDimPermissionsOverageDefault),
  isOverridden: yup.boolean().label(ProductCatalogItemLabels.isOverridden),
  isActive: yup.boolean(),
  customerXRef: yup.object()
});

export interface ProductCatalogItem {
  auditMessages: string[];
  business: Business;
  category?: any;
  createdByFullName: string;
  createdByUserId: string;
  createdDateTime: string;
  dataInheritanceLevel: string;
  defaultConfigType: string;
  defaultOverageOwner: string;
  deploymentType: string;
  division?: Division;
  documentType: string;
  id: string;
  isActive: boolean;
  isCommonStock: boolean;
  isDeleted: boolean;
  isOverridden: boolean;
  manufacturer?: any;
  modifiedByFullName: string;
  modifiedByUserId: string;
  modifiedDateTime: string;
  partitionKey: string;
  partitionKeyDescription: string;
  defaultOverageCondition: string;
  defaultOverageDisposition: string;
  productName?: any;
  alternateItemId?: string;
  productNumber: string;
  salesCertReconPrice: number;
  salesNewPrice: number;
  salesUsedPrice: number;
  isWebOrderDimPermissionsOverageDefault: boolean;
  customerXRef?: {
    alternateItemId?: string;
    alternateItemIdLabel?: string;
    customerStandardCost?: number;
    modifiedDateTime?: string;
  };
}

interface Division {
  erpId: string;
  id: string;
  name: string;
}

interface Business {
  dataAreaId: string;
  erpId: string;
  id: string;
  name: string;
}
